import React, { useEffect, useState } from "react";
import { Box, Heading, Text, Spinner, VStack } from "@chakra-ui/react";

const CurrentLocationSaver: React.FC = () => {
    const [bestCoords, setBestCoords] = useState<GeolocationCoordinates | null>(null);
    const [locationName, setLocationName] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser.");
            return;
        }
        if (!API_KEY) {
            setError("Google Maps API key is missing.");
            return;
        }

        let bestPosition: GeolocationPosition | null = null;

        const watcherId = navigator.geolocation.watchPosition(
            (position) => {
                // Update best position if accuracy improves
                if (!bestPosition || position.coords.accuracy < bestPosition.coords.accuracy) {
                    bestPosition = position;
                    setBestCoords(position.coords);

                    // If accuracy is good enough, stop watching early and reverse geocode
                    if (position.coords.accuracy < 10) {
                        navigator.geolocation.clearWatch(watcherId);
                        reverseGeocode(position.coords.latitude, position.coords.longitude);
                    }
                }
            },
            (posError) => {
                setError(`Geolocation error: ${posError.message}`);
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 60000 }
        );

        const reverseGeocode = async (lat: number, lng: number) => {
            try {
                const response = await fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`
                );
                const data = await response.json();
                if (data.status === "OK" && data.results.length > 0) {
                    setLocationName(data.results[0].formatted_address);
                } else {
                    setLocationName("No address found");
                }
            } catch {
                setLocationName("Error fetching address");
            }
        };

        // Stop watching after 15 seconds max and reverse geocode bestPosition
        const timeoutId = setTimeout(() => {
            navigator.geolocation.clearWatch(watcherId);
            if (bestPosition) {
                reverseGeocode(bestPosition.coords.latitude, bestPosition.coords.longitude);
            } else {
                setError("Could not get location within timeout.");
            }
        }, 15000);

        return () => {
            clearTimeout(timeoutId);
            navigator.geolocation.clearWatch(watcherId);
        };
    }, [API_KEY]);

    return (
        <Box p={6} maxW="md" mx="auto" textAlign="center" boxShadow="md" borderRadius="md" mt={10}>
            <Heading mb={4}>Current Location Saver</Heading>

            {error && (
                <Text color="red.500" fontWeight="bold" mb={4}>
                    {error}
                </Text>
            )}

            {!error && !bestCoords && <Spinner size="xl" label="Getting location..." />}

            {bestCoords && (
                <VStack spacing={3}>
                    <Text fontSize="lg">
                        <b>Coordinates:</b> {bestCoords.latitude.toFixed(7)}, {bestCoords.longitude.toFixed(7)}
                    </Text>
                    <Text fontSize="md" color="gray.600">
                        ±{bestCoords.accuracy.toFixed(2)} meters accuracy
                    </Text>
                </VStack>
            )}

            {bestCoords && !locationName && <Spinner mt={4} label="Fetching address..." />}

            {locationName && (
                <Text mt={4} fontSize="md" fontWeight="semibold" color="blue.600">
                    {locationName}
                </Text>
            )}
        </Box>
    );
};

export default CurrentLocationSaver;
